import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIf, NgStyle } from '@angular/common';
import { IconComponent } from './icon.component';

type Variant = 'primary' | 'deep' | 'secondary' | 'soft' | 'ghost' | 'dark';
type Size = 'sm' | 'md' | 'lg';

const SIZES: Record<Size, { h: number; px: number; fs: number; r: number }> = {
  sm: { h: 38, px: 14, fs: 14, r: 11 },
  md: { h: 48, px: 18, fs: 15.5, r: 13 },
  lg: { h: 56, px: 22, fs: 17, r: 15 },
};
const VARIANTS: Record<Variant, Record<string, string>> = {
  primary: { background: 'var(--brand)', color: '#fff', boxShadow: '0 6px 16px -6px var(--brand-shadow)' },
  deep: { background: 'var(--brand-ink)', color: '#fff' },
  secondary: { background: 'var(--fr-surface)', color: 'var(--fr-text)', boxShadow: 'inset 0 0 0 1.5px var(--fr-border-2)' },
  soft: { background: 'var(--brand-tint)', color: 'var(--brand-deep)' },
  ghost: { background: 'transparent', color: 'var(--brand-deep)' },
  dark: { background: '#1c1a17', color: '#fff' },
};

/** Primary action button with variants/sizes and optional leading/trailing icons. */
@Component({
  selector: 'app-btn',
  standalone: true,
  imports: [NgIf, NgStyle, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<button
      [disabled]="disabled"
      [ngStyle]="btnStyle"
      (click)="onClick($event)"
      (mousedown)="press($event, true)"
      (mouseup)="press($event, false)"
      (mouseleave)="press($event, false)">
      <app-icon *ngIf="icon" [name]="icon!" [size]="sz.fs + 3"></app-icon>
      <ng-content></ng-content>
      <app-icon *ngIf="iconEnd" [name]="iconEnd!" [size]="sz.fs + 3"></app-icon>
    </button>`,
})
export class BtnComponent {
  @Input() variant: Variant = 'primary';
  @Input() size: Size = 'md';
  @Input() full = false;
  @Input() disabled = false;
  @Input() icon?: string;
  @Input() iconEnd?: string;
  @Output() act = new EventEmitter<MouseEvent>();

  get sz() { return SIZES[this.size]; }

  get btnStyle(): Record<string, string> {
    return {
      height: this.sz.h + 'px',
      padding: `0 ${this.sz.px}px`,
      borderRadius: this.sz.r + 'px',
      fontSize: this.sz.fs + 'px',
      fontWeight: '700',
      border: 'none',
      cursor: this.disabled ? 'not-allowed' : 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: this.full ? '100%' : '',
      fontFamily: 'var(--fr-font)',
      letterSpacing: '-0.01em',
      opacity: this.disabled ? '0.45' : '1',
      transition: 'transform .12s ease, filter .15s ease',
      ...VARIANTS[this.variant],
    };
  }

  onClick(e: MouseEvent): void {
    if (!this.disabled) this.act.emit(e);
  }
  press(e: MouseEvent, down: boolean): void {
    if (this.disabled) return;
    (e.currentTarget as HTMLElement).style.transform = down ? 'scale(0.97)' : 'scale(1)';
  }
}
